/**
 * AuditTrail.js — Semantic Audit Trail viewer for CRM Admin
 *
 * Fetches from /api/audit-logs (app='crm' scoped by backend).
 * Supports filtering by person, action, entity type, role, and date range.
 * Click a row to expand the old→new diff.
 */
import React, { useState, useCallback, useEffect, useMemo } from "react";
import API from "../utils/api";

const ACTION_LABELS = {
  "lead.create":          { icon: "➕", label: "Lead Created",        color: "#22c55e" },
  "lead.update":          { icon: "✏️", label: "Lead Updated",        color: "#3b82f6" },
  "lead.stage_change":    { icon: "🔄", label: "Stage Changed",       color: "#8b5cf6" },
  "lead.close_lost":      { icon: "❌", label: "Closed Lost",         color: "#ef4444" },
  "checkin.create":       { icon: "📍", label: "Check-in Recorded",   color: "#06b6d4" },
};

function actionMeta(action) {
  return ACTION_LABELS[action] || { icon: "📋", label: action || "—", color: "#64748b" };
}

function RoleBadge({ role }) {
  const colors = {
    superadmin: "#7c3aed", admin: "#2563eb", manager: "#059669",
    team_leader: "#0284c7", employee: "#64748b",
  };
  const c = colors[role] || "#475569";
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 99,
      fontSize: 11, fontWeight: 600,
      background: c + "22", color: c, border: `1px solid ${c}44`,
      textTransform: "capitalize",
    }}>
      {role || "—"}
    </span>
  );
}

function DiffView({ oldValue, newValue }) {
  let o, n;
  try { o = typeof oldValue === "string" ? JSON.parse(oldValue) : oldValue; } catch { o = null; }
  try { n = typeof newValue === "string" ? JSON.parse(newValue) : newValue; } catch { n = null; }
  if (!o && !n) return null;
  const keys = Array.from(new Set([...(o ? Object.keys(o) : []), ...(n ? Object.keys(n) : [])]));
  const changed = keys.filter(k => JSON.stringify(o?.[k] ?? null) !== JSON.stringify(n?.[k] ?? null));
  if (!changed.length) {
    return <pre style={{ margin: 0, fontSize: 11, color: "#94a3b8", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{JSON.stringify(n || o, null, 2)}</pre>;
  }
  return (
    <table style={{ borderCollapse: "collapse", fontSize: 11, width: "100%" }}>
      <tbody>
        {changed.map(k => (
          <tr key={k}>
            <td style={{ paddingRight: 8, color: "#94a3b8", whiteSpace: "nowrap", verticalAlign: "top" }}>{k}</td>
            <td style={{ color: "#fca5a5", textDecoration: "line-through", paddingRight: 8, verticalAlign: "top" }}>{o?.[k] != null ? String(o[k]).slice(0, 60) : "—"}</td>
            <td style={{ color: "#86efac", verticalAlign: "top" }}>{n?.[k] != null ? String(n[k]).slice(0, 60) : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function AuditTrail({ accent = "#E31E24" }) {
  const [logs, setLogs]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [fetched, setFetched]   = useState(false);
  const [companionFlags, setCompanionFlags] = useState([]);
  const [gpsSummaries, setGpsSummaries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filters, setFilters]   = useState({
    company: "", user_id: "", action: "", entity_type: "", effective_role: "",
    date_from: "", date_to: "",
  });

  useEffect(() => {
    API.get("/users")
      .then(({ data }) => setEmployees((Array.isArray(data) ? data : []).filter(user =>
        user.is_active !== 0 && ["ebek", "ebekmed"].includes(String(user.company || "").toLowerCase())
      )))
      .catch(() => setEmployees([]));
  }, []);

  const companies = useMemo(
    () => Array.from(new Set(employees.map(user => String(user.company || "").toLowerCase())))
      .filter(company => company === "ebek" || company === "ebekmed"),
    [employees]
  );

  const visibleEmployees = useMemo(
    () => employees.filter(user => !filters.company || String(user.company || "").toLowerCase() === filters.company),
    [employees, filters.company]
  );

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setFetched(true);
    try {
      const params = new URLSearchParams({ limit: 300 });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const { data } = await API.get(`/audit-logs?${params}`);
      setLogs(data.logs || []);
      try {
      const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
      const rangeFrom = filters.date_from || filters.date_to || today;
      const rangeTo = filters.date_to || filters.date_from || today;
      const dates = [];
      const cursor = new Date(`${rangeFrom}T00:00:00`);
      const end = new Date(`${rangeTo}T00:00:00`);
      while (cursor <= end && dates.length < 31) {
        dates.push(cursor.toISOString().slice(0, 10));
        cursor.setDate(cursor.getDate() + 1);
      }
      await Promise.all(dates.map(date => API.post(`/location-logs/analyze-companions?date=${date}`, { date })));
      const gpsResponses = await Promise.all(dates.map(date => API.get(`/location-logs/summary?date=${date}`)));
      const selectedUserId = Number(filters.user_id || 0);
      setGpsSummaries(gpsResponses.flatMap(response => response.data?.summary || []).filter(row =>
        (!filters.company || String(row.company || "").toLowerCase() === filters.company)
        && (!selectedUserId || Number(row.user_id) === selectedUserId)
      ));
      const companionRes = await API.get(`/location-logs/companion-flags?from=${rangeFrom}&to=${rangeTo}`);
      setCompanionFlags((companionRes.data.flags || []).filter(flag =>
        !selectedUserId || Number(flag.user_id_a) === selectedUserId || Number(flag.user_id_b) === selectedUserId
      ));
      } catch {
        setCompanionFlags([]);
        setGpsSummaries([]);
      }
    } catch {
      setLogs([]);
      setCompanionFlags([]);
      setGpsSummaries([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchLogs();
  }, []); // Load the latest employee-wise audit trail when the tab opens.

  function handleFilter(e) {
    setFilters(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  function fmtTime(ts) {
    if (!ts) return "—";
    const d = new Date(ts);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
      + " " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  }

  const inp = {
    background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8,
    color: "#1e293b", fontSize: 13, padding: "7px 12px", outline: "none", minWidth: 130,
  };

  return (
    <div style={{ padding: "20px 0" }}>
      <h3 style={{ color: "#1e293b", fontSize: 16, fontWeight: 700, margin: "0 0 16px" }}>
        🔍 CRM Audit Trail
      </h3>

      {/* Filter bar */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16, alignItems: "flex-end" }}>
        <select
          style={inp}
          name="company"
          value={filters.company}
          onChange={e => setFilters(f => ({ ...f, company: e.target.value, user_id: "" }))}
        >
          <option value="">All companies</option>
          {companies.map(company => <option key={company} value={company}>{company === "ebekmed" ? "Ebek Med" : "Ebek"}</option>)}
        </select>
        <select style={{ ...inp, minWidth: 210 }} name="user_id" value={filters.user_id} onChange={handleFilter}>
          <option value="">All employees</option>
          {visibleEmployees.map(employee => (
            <option key={employee.id} value={employee.id}>{employee.name}</option>
          ))}
        </select>
        <input style={inp} name="action"      placeholder="Action"          value={filters.action}      onChange={handleFilter} />
        <input style={inp} name="entity_type" placeholder="Entity type"     value={filters.entity_type} onChange={handleFilter} />
        <select style={inp} name="effective_role" value={filters.effective_role} onChange={handleFilter}>
          <option value="">All roles</option>
          <option value="superadmin">Superadmin</option>
          <option value="admin">Admin</option>
          <option value="manager">Manager</option>
          <option value="team_leader">Team Leader</option>
          <option value="employee">Employee</option>
        </select>
        <input style={inp} type="date" name="date_from" value={filters.date_from} onChange={handleFilter} />
        <input style={inp} type="date" name="date_to"   value={filters.date_to}   onChange={handleFilter} />
        <button
          onClick={fetchLogs}
          style={{ background: accent, color: "#fff", border: "none", borderRadius: 8, padding: "8px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
        >
          Search
        </button>
        <button
          onClick={() => {
            setFilters({ company: "", user_id: "", action: "", entity_type: "", effective_role: "", date_from: "", date_to: "" });
            setTimeout(fetchLogs, 50);
          }}
          style={{ background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 18px", fontSize: 13, cursor: "pointer" }}
        >
          Clear
        </button>
      </div>

      {!fetched && (
        <div style={{ color: "#94a3b8", padding: "40px 0", textAlign: "center" }}>
          Press <b>Search</b> to load audit events
        </div>
      )}

      {fetched && (
        <>
          {loading && <div style={{ color: "#94a3b8", marginBottom: 12 }}>Loading…</div>}
          <EmployeeGpsAudit rows={gpsSummaries} />
          <CompanionGpsAudit flags={companionFlags} />
          <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 12 }}>
            {logs.length} event{logs.length !== 1 ? "s" : ""} (max 300)
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["When","Person","Acting as","Action","Record","Changes"].map(h => (
                    <th key={h} style={{ color: "#94a3b8", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", textAlign: "left", padding: "8px 12px", borderBottom: "1px solid #f1f5f9" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && !loading && (
                  <tr><td colSpan={6} style={{ color: "#cbd5e1", textAlign: "center", padding: 40 }}>No audit events found</td></tr>
                )}
                {logs.map(log => {
                  const meta = actionMeta(log.action);
                  const isOpen = expanded === log.id;
                  return (
                    <tr
                      key={log.id}
                      onClick={() => setExpanded(isOpen ? null : log.id)}
                      style={{ cursor: "pointer", background: isOpen ? "#f8fafc" : "transparent" }}
                    >
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #f1f5f9", fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>{fmtTime(log.created_at)}</td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #f1f5f9" }}>
                        <div style={{ fontWeight: 600, color: "#1e293b" }}>{log.user_name || "—"}</div>
                        {log.ip && <div style={{ fontSize: 11, color: "#94a3b8" }}>{log.ip}</div>}
                      </td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #f1f5f9" }}>
                        <RoleBadge role={log.effective_role || log.user_role} />
                      </td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #f1f5f9" }}>
                        <span style={{ color: meta.color, fontWeight: 600 }}>{meta.icon} {meta.label}</span>
                      </td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155" }}>
                        {log.entity_label || `${log.entity_type} #${log.entity_id}`}
                      </td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid #f1f5f9" }}>
                        {isOpen
                          ? <DiffView oldValue={log.old_value} newValue={log.new_value} />
                          : (log.old_value || log.new_value)
                            ? <span style={{ color: "#94a3b8", fontSize: 11 }}>Click to expand ›</span>
                            : <span style={{ color: "#e2e8f0" }}>—</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function EmployeeGpsAudit({ rows }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 14, marginBottom: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: "#1e293b", marginBottom: 3 }}>Employee GPS / KMS audit</div>
      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 10 }}>Route km is login → every visit → log-off in a straight line. GPS trail km is the distance between background location pings, shown for reference.</div>
      {!rows.length ? (
        <div style={{ padding: "14px 2px", color: "#94a3b8", fontSize: 12 }}>No GPS audit records found for the selected employee and dates.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>{["Date", "Employee", "Company", "Work type", "Visits", "GPS pings", "Route KM", "GPS trail KM"].map(label => (
                <th key={label} style={{ textAlign: "left", padding: "8px 10px", color: "#64748b", borderBottom: "1px solid #e2e8f0" }}>{label}</th>
              ))}</tr>
            </thead>
            <tbody>{rows.map((row, index) => (
              <tr key={`${row.user_id}-${row.date || index}`}>
                <td style={{ padding: "9px 10px", borderBottom: "1px solid #f1f5f9" }}>{row.date || "-"}</td>
                <td style={{ padding: "9px 10px", borderBottom: "1px solid #f1f5f9", fontWeight: 700 }}>{row.name}</td>
                <td style={{ padding: "9px 10px", borderBottom: "1px solid #f1f5f9" }}>{row.company === "ebekmed" ? "Ebek Med" : "Ebek"}</td>
                <td style={{ padding: "9px 10px", borderBottom: "1px solid #f1f5f9" }}>{row.plan_type || "-"}</td>
                <td style={{ padding: "9px 10px", borderBottom: "1px solid #f1f5f9" }}>{row.visit_count || 0}</td>
                <td style={{ padding: "9px 10px", borderBottom: "1px solid #f1f5f9" }}>{row.ping_count || 0}</td>
                <td style={{ padding: "9px 10px", borderBottom: "1px solid #f1f5f9", fontWeight: 800 }}>{Number(row.total_km || 0).toFixed(2)} km</td>
                <td style={{ padding: "9px 10px", borderBottom: "1px solid #f1f5f9", color: "#64748b" }}>{Number(row.gps_trail_km || 0).toFixed(2)} km</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CompanionGpsAudit({ flags }) {
  const variations = flags.filter(flag => Number(flag.flagged) === 1).length;
  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 14, marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#1e293b" }}>Companion GPS audit</div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>Full-day comparison for employees recorded together on institution visits. A separation above 500m is captured as a variation.</div>
        </div>
        <span style={{ background: variations ? "#fee2e2" : "#dcfce7", color: variations ? "#dc2626" : "#15803d", borderRadius: 99, padding: "5px 10px", fontSize: 11, fontWeight: 800 }}>
          {variations} variation{variations === 1 ? "" : "s"}
        </span>
      </div>
      {!flags.length ? (
        <div style={{ padding: "14px 2px", color: "#94a3b8", fontSize: 12 }}>No companion visits found for this date range.</div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {flags.map(flag => {
            const flagged = Number(flag.flagged) === 1;
            const day = String(flag.date || "").slice(0, 10);
            return (
              <div key={flag.id} style={{ display: "grid", gridTemplateColumns: "minmax(170px,1.4fr) minmax(90px,.6fr) minmax(105px,.6fr) minmax(230px,2fr)", gap: 10, alignItems: "center", padding: 11, border: `1px solid ${flagged ? "#fecaca" : "#bbf7d0"}`, borderRadius: 10, background: flagged ? "#fff7f7" : "#f7fff9", fontSize: 12, overflowX: "auto" }}>
                <div><strong style={{ color: "#1e293b" }}>{flag.name_a}</strong><span style={{ color: "#94a3b8" }}> + </span><strong style={{ color: "#1e293b" }}>{flag.name_b}</strong></div>
                <div style={{ color: "#64748b" }}>{day ? new Date(`${day}T00:00:00`).toLocaleDateString("en-IN") : "-"}</div>
                <div style={{ color: flagged ? "#dc2626" : "#15803d", fontWeight: 800 }}>{Number(flag.max_separation_m || 0).toLocaleString("en-IN")} m max</div>
                <div style={{ color: flagged ? "#b91c1c" : "#15803d", fontWeight: 650 }}>{flagged ? flag.flag_reason : `GPS matched throughout (${flag.overlap_minutes || 0} min compared)`}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
